// media-device.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AvailableDevices, MediaDevice } from '../models/device';
import { AppConfigService } from './app.config.service';
import { FELoggerService, LogSource } from './feLogger.service';
import { I18Service } from './i18.service';

export interface MediaDeviceState {
  audioInputs: MediaDevice[];
  audioOutputs: MediaDevice[];
  videoInputs: MediaDevice[];
//   isProcessing: boolean;
}

export interface PreferredDevices {
  audioInput: string | null;
  audioOutput: string | null;
  camera: string | null;
}

export interface DeviceChangeEvent {
  timestamp: number;
  devices: AvailableDevices;
  changes: DeviceChanges;
  changeType: DeviceChangeType;
}

export interface DeviceChanges {
  audioInputChanged: boolean;
  audioOutputChanged: boolean;
  videoInputChanged: boolean;
  addedDevices: MediaDevice[];
  removedDevices: MediaDevice[];
}

export type DeviceChangeType =
  | 'headphones'
  | 'videoinput'
  | 'audioinput'
  | 'audiooutput'
  | 'multiple'
  | 'unknown';

interface DeviceSnapshot {
  audioInputs: Set<MediaDevice>;
  audioOutputs: Set<MediaDevice>;
  videoInputs: Set<MediaDevice>;
}

const DEBOUNCE_DELAY = 500;
const DEFAULT_DEVICE = "_default_"; // Default value for device selection

@Injectable({
  providedIn: 'root'
})
export class MediaDeviceService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private deviceChangeSubject$ = new Subject<DeviceChangeEvent>();
  
  private static get storageKey() { return `${AppConfigService.role}.DevicesPref`;}

  // Observable pubblico per notifiche di cambio dispositivo
  public deviceChange$ = this.deviceChangeSubject$.asObservable();

  // Stato interno corrente (source of truth)
  private currentState: MediaDeviceState = {
    audioInputs: [],
    audioOutputs: [],
    videoInputs: []
  };

  // Device selection state (dispositivi attualmente selezionati per uso)
  private selectedDevices = {
    videoSourceId: null as string | null,
    audioInSourceId: null as string | null,
    audioOutSourceId: null as string | null,
    cameraFacing: 'user' as string
  };

  // Device capabilities (informazioni sullo stato dei dispositivi)
  private deviceCapabilities = {
    isMultiCamera: false,
    atLeastACamera: false
  };

    // User preferred devices (preferenze utente salvate in localStorage)
  private userPreferredDevices: PreferredDevices = {
    audioInput: null,
    audioOutput: null,
    camera: null
  };

  // Getter sincroni per accesso immediato allo stato dispositivi disponibili
  public get audioInputs(): MediaDevice[] {
    return this.currentState.audioInputs;
  }

  public get audioOutputs(): MediaDevice[] {
    return this.currentState.audioOutputs;
  }

  public get videoInputs(): MediaDevice[] {
    return this.currentState.videoInputs;
  }

  public getDevices(): MediaDeviceState {
    return { ...this.currentState };
  }

  // Getter sincroni per dispositivi selezionati
  public get videoSource(): string | null {
    const selected = this.selectedDevices.videoSourceId;
    if (!this.videoInputs.some( z => z.deviceId == selected )) return null; // se non è più disponibile
    return selected;
  }

  public get audioInputSource(): string | null {
    const selected = this.selectedDevices.audioInSourceId;
    if (!this.audioInputs.some( z => z.deviceId == selected )) return null; // se non è più disponibile
    return selected;
  }

  public get audioOutputSource(): MediaDevice | null {
    const selected = this.selectedDevices.audioOutSourceId;
    return this.getAudioOutputMediaDevice(selected);
  }
  public getAudioOutputMediaDevice(deviceId: string | null): MediaDevice | null {
    if (deviceId == null) return null;
    return this.audioOutputs.find( z => z.deviceId == deviceId ) || null;

  }

  public get cameraFacing(): string {
    return this.selectedDevices.cameraFacing;
  }

  // Getter sincroni per capabilities
  public get isMultiCamera(): boolean {
    return this.deviceCapabilities.isMultiCamera;
  }

  public get atLeastACamera(): boolean {
    return this.deviceCapabilities.atLeastACamera;
  }

  // Stato interno
  private previousSnapshot: DeviceSnapshot | null = null;
   // ms
  
  constructor(private config: AppConfigService) {
    // this.initialize();
  }
  
  public init(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            FELoggerService.error('MediaDevices API non supportata');
            reject();
            return;
        }
        console.log('MediaDeviceService initialize');
        
        // Carica dispositivi iniziali
        this.updateDeviceList().then(() => {resolve();});
        
        // Ascolta i cambiamenti con debounce
        if(!this.config.isCaller)
            this.setupDeviceChangeListener();
    });
  }
  
  private eventBuffer: number = 0;
  private emitTimer: any;
  private setupDeviceChangeListener(): void {
    const deviceChange$ = fromEvent(navigator.mediaDevices, 'devicechange');
    
    deviceChange$
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => {
            console.log('mediaDeviceService - devicechange event detected', event);
            this.eventBuffer++;

            clearTimeout(this.emitTimer);
            this.emitTimer = setTimeout(() => {
                const count = this.eventBuffer;
                this.eventBuffer = 0;
                this.handleDeviceChange(Date.now(), count);
            }, DEBOUNCE_DELAY);
        });
  }
  
  private async handleDeviceChange(timestamp: number, count: number): Promise<void> {
    // this.updateState({ isProcessing: true });
    // console.log(`DEBUG- Device change detected: ${count}`);
    
    try {
      // Ottieni dispositivi attuali
      const currentDevices = await this.getAllDevices();
      const currentSnapshot = this.createSnapshot(currentDevices);
      
      // Calcola i cambiamenti
      const changes = this.previousSnapshot 
        ? this.detectChanges(this.previousSnapshot, currentSnapshot, currentDevices)
        : this.createInitialChanges();
      
      console.log('mediaDeviceService - changes', changes);
      // Determina il tipo di cambiamento
      const changeType = this.determineChangeType(changes);
      
      // Aggiorna lo snapshot
      this.previousSnapshot = currentSnapshot;
      
      // Emetti l'evento
      const event: DeviceChangeEvent = {
        timestamp,
        devices: currentDevices,
        changes,
        changeType
      };
      
      this.deviceChangeSubject$.next(event);
      
      // Aggiorna lo stato
      this.updateState({
        ...currentDevices,
        // isProcessing: false
      });
      
    //   console.log(`DEBUG- [${count} events] Device change: ${changeType}`, changes);
      
    } catch (error) {
      FELoggerService.error('mediaDeviceService - Error handling device change', LogSource.MediaAudio, error);
    }
  }
  
  private createSnapshot(devices: AvailableDevices): DeviceSnapshot {
    return {
      audioInputs: new Set(devices.audioInputs),
      audioOutputs: new Set(devices.audioOutputs),
      videoInputs: new Set(devices.videoInputs),
    };
  }
  
  private detectChanges(
    previous: DeviceSnapshot,
    current: DeviceSnapshot,
    currentDevices: AvailableDevices
  ): DeviceChanges {
    // Trova dispositivi aggiunti
    const addedAudioInputs = [...current.audioInputs].filter(curr => ![...previous.audioInputs].some(prev => prev.deviceId === curr.deviceId));
    const addedAudioOutputs = [...current.audioOutputs].filter(curr => ![...previous.audioOutputs].some(prev => prev.deviceId === curr.deviceId));
    const addedVideoInputs = [...current.videoInputs].filter(curr => ![...previous.videoInputs].some(prev => prev.deviceId === curr.deviceId));

    // Trova dispositivi rimossi
    const removedAudioInputs = [...previous.audioInputs].filter(prev => ![...current.audioInputs].some(curr => curr.deviceId === prev.deviceId));
    const removedAudioOutputs = [...previous.audioOutputs].filter(prev => ![...current.audioOutputs].some(curr => curr.deviceId === prev.deviceId));
    const removedVideoInputs = [...previous.videoInputs].filter(prev => ![...current.videoInputs].some(curr => curr.deviceId === prev.deviceId));

    const addedDevices = [
      ...addedAudioInputs,
      ...addedAudioOutputs,
      ...addedVideoInputs
    ];

    const removedDevices = [
      ...removedAudioInputs,
      ...removedAudioOutputs,
      ...removedVideoInputs
    ];

    return {
      audioInputChanged: addedAudioInputs.length > 0 || removedAudioInputs.length > 0,
      audioOutputChanged: addedAudioOutputs.length > 0 || removedAudioOutputs.length > 0,
      videoInputChanged: addedVideoInputs.length > 0 || removedVideoInputs.length > 0,
      addedDevices,
      removedDevices
    };
  }
  
  private createInitialChanges(): DeviceChanges {
    return {
      audioInputChanged: false,
      audioOutputChanged: false,
      videoInputChanged: false,
      addedDevices: [],
      removedDevices: []
    };
  }
  
  private determineChangeType(changes: DeviceChanges): DeviceChangeType {
    const { audioInputChanged, audioOutputChanged, videoInputChanged } = changes;
    
    // Conta quanti tipi di dispositivi sono cambiati
    const changedCount = [audioInputChanged, audioOutputChanged, videoInputChanged]
      .filter(Boolean).length;
    
    // Logica migliorata per identificare le cuffie
    if (audioInputChanged && audioOutputChanged && !videoInputChanged) {
      // Molto probabilmente sono cuffie con microfono
      return 'headphones';
    }
    
    if (videoInputChanged) return 'videoinput';    

    // Singoli cambiamenti
    if (changedCount === 1) {
      if (audioInputChanged) return 'audioinput';
      if (audioOutputChanged) return 'audiooutput';
    }
    
    // Cambiamenti multipli non identificabili come cuffie
    if (changedCount > 1) {
      return 'multiple';
    }
    
    return 'unknown';
  }
  
  private async getAllDevices(): Promise<AvailableDevices> {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();

        const audioInputDevices: MediaDevice[] = [];
        const audioOutputDevices: MediaDevice[] = []; 
        const videoDevices: MediaDevice[] = [];
        var mobileVideoInserted = false;
        console.debug('mediaDeviceService - Enumerating devices', devices);

        for (let i = 0; i !== devices.length; ++i) {
            const deviceInfo = devices[i];

            switch (deviceInfo.kind) {
                case 'audioinput':
                    // deviceInfo.label = deviceInfo.label || `microphone ${audioInputDevices.length + 1}`;
                    if (!audioInputDevices.some(z => z.groupId && z.groupId == deviceInfo.groupId)) {
                        audioInputDevices.push(deviceInfo);
                    }
                    break;
                case 'audiooutput':
                    // deviceInfo.label = deviceInfo.label || `speaker ${audioOutputDevices.length + 1}`;
                    if (!audioOutputDevices.some(z => z.groupId && z.groupId == deviceInfo.groupId)) {
                        audioOutputDevices.push(deviceInfo);
                    }
                    break;
                case 'videoinput':
                    // deviceInfo.label = deviceInfo.label || `camera ${videoDevices.length + 1}`;
                    if (!videoDevices.some(z => z.groupId && z.groupId == deviceInfo.groupId)) {

                        if(this.config.isMobilePhone && deviceInfo.label){
                            if (!mobileVideoInserted ) {
                                mobileVideoInserted = true;
                                const frontCamera: MediaDevice =  {
                                    deviceId: 'user',
                                    groupId: 'frontcamera',
                                    kind: 'videoinput',
                                    label: I18Service.get('video.webcam.front'),
                                };
                                videoDevices.push(frontCamera);
                                const backCamera: MediaDevice = {
                                    deviceId: 'environment',
                                    groupId: 'backcamera',
                                    kind: 'videoinput',
                                    label: I18Service.get('video.webcam.back'),
                                };
                                videoDevices.push(backCamera);
                            }
                        } else {
                            videoDevices.push(deviceInfo);
                        }
                        // videoDevices.push(deviceInfo);
                    }
                    break;
                default:                            
                    console.log('mediaService - Some other kind of source/device: ', deviceInfo);//, Framework.MediaAudio);
                    break;
                
            }
        }

      
        return {
            audioInputs: audioInputDevices,
            audioOutputs: audioOutputDevices,
            videoInputs: videoDevices
        };
    } catch (error) {
    //   console.error('Errore nel recupero dei dispositivi:', error);
      FELoggerService.error('mediaDeviceService - Error Enumerating devices', LogSource.MediaAudio, error)
      return { audioInputs: [], audioOutputs: [], videoInputs: [] };
    }
  }
  
  private updateState(partial: Partial<MediaDeviceState>): void {
    // Aggiorna lo stato interno (source of truth)
    this.currentState = {
      audioInputs: [],
      audioOutputs: [],
      videoInputs: [],
    //   isProcessing: false,
      ...partial
    };
  }
  
  // Metodi pubblici di utilità
  
  /**
   * Aggiorna manualmente la lista dei dispositivi
   */
  public async updateDeviceList(): Promise<void> {
    const devices = await this.getAllDevices();
    const snapshot = this.createSnapshot(devices);
    this.previousSnapshot = snapshot;

    this.updateState({
      ...devices,
    });

    // Aggiorna anche i dispositivi selezionati e le capabilities
    this.updateSelectedDevices();
  }

  /**
   * Aggiorna i dispositivi selezionati basandosi su preferenze utente + dispositivi disponibili
   * Questo metodo implementa la logica di selezione automatica dei dispositivi
   */
  public updateSelectedDevices(force = false): void {
    // Aggiorna capabilities
    this.deviceCapabilities.isMultiCamera = this.videoInputs.length > 1;
    this.deviceCapabilities.atLeastACamera = this.videoInputs.length > 0;

    const isInCall = this.config.iamOnCall;
    if (isInCall && !force) { return;}

    const firstVideo = this.getDefaultDevice('videoinput');
    const firstAudioOut = this.getDefaultDevice('audiooutput');
    const firstAudioIn = this.getDefaultDevice('audioinput');

    // ===== VIDEO INPUT =====
    // Se l'utente ha una preferenza e il dispositivo è disponibile, usalo
    const prefCamera=this.userPreferredDevices.camera;
    if (prefCamera && this.videoInputs.find(z => z.deviceId === prefCamera)) {
      this.selectedDevices.videoSourceId = prefCamera;
      // Su mobile con facingMode, NON sovrascrivere cameraFacing
      // È gestito da switchCamera() e deve rimanere 'user' o 'environment'
      // if (this.config.isMobilePhone) {
      //   this.selectedDevices.cameraFacing = prefCamera;
      // }
    } else {
      // Altrimenti usa il primo disponibile come fallback
      if (firstVideo) {
        this.selectedDevices.videoSourceId = firstVideo.deviceId;
        // Su mobile con facingMode, NON sovrascrivere cameraFacing
        // È gestito da switchCamera() e deve rimanere 'user' o 'environment'
        // if (this.config.isMobilePhone) {
        //   this.selectedDevices.cameraFacing = firstVideo.deviceId;
        // }
      }
    }

    // ===== AUDIO OUTPUT =====
    // Se l'utente ha una preferenza e il dispositivo è disponibile, usalo
    const prefAudioOut=this.userPreferredDevices.audioOutput;
    if (prefAudioOut &&
        this.audioOutputs.find(z => z.deviceId === prefAudioOut)) {
      this.selectedDevices.audioOutSourceId = prefAudioOut;
    } else {
      // Altrimenti usa il primo disponibile come fallback
      this.selectedDevices.audioOutSourceId = firstAudioOut?.deviceId || null;
    }

    // ===== AUDIO INPUT =====
    // Se l'utente ha una preferenza e il dispositivo è disponibile, usalo
    const prefAudioIn=this.userPreferredDevices.audioInput;
    if (prefAudioIn &&
        this.audioInputs.find(z => z.deviceId === prefAudioIn)) {
      this.selectedDevices.audioInSourceId = prefAudioIn;
    } else {
      // Altrimenti usa il primo disponibile come fallback
      this.selectedDevices.audioInSourceId = firstAudioIn?.deviceId || '';
    }

    // Log per debugging
    console.log(`MediaDeviceService - updateSelectedDevices:
      isMultiCamera: ${this.deviceCapabilities.isMultiCamera}, atLeastACamera: ${this.deviceCapabilities.atLeastACamera}
      Video: ${this.selectedDevices.videoSourceId} (${firstVideo?.label})
      Audio In: ${this.selectedDevices.audioInSourceId} (${firstAudioIn?.label})
      Audio Out: ${this.selectedDevices.audioOutSourceId} (${firstAudioOut?.label})
    `);
  }
  
  /**
   * Verifica se un dispositivo specifico è connesso
   */
  public async isDeviceConnected(deviceId: string): Promise<boolean> {
    const devices = await this.getAllDevices();
    const allDevices = [
      ...devices.audioInputs,
      ...devices.audioOutputs,
      ...devices.videoInputs
    ];
    return allDevices.some(d => d.deviceId === deviceId);
  }
  
  /**
   * Ottiene il dispositivo predefinito per tipo (sincrono, usa lo stato corrente)
   */
  public getDefaultDevice(kind: MediaDeviceKind): MediaDevice | undefined {
    let deviceList: MediaDevice[] = [];

    switch (kind) {
      case 'audioinput':
        deviceList = this.currentState.audioInputs;
        break;
      case 'audiooutput':
        deviceList = this.currentState.audioOutputs;
        break;
      case 'videoinput':
        deviceList = this.currentState.videoInputs;
        break;
    }

    // Il primo dispositivo è solitamente quello predefinito
    return deviceList[0];
  }

  /**
   * Ottiene l'etichetta del dispositivo predefinito per tipo
   */
  public getDefaultDeviceLabel(kind: MediaDeviceKind): string {
    return this.getDefaultDevice(kind)?.label || `${kind} undefined`;
  }
  
  /**
   * Controlla se ci sono cuffie connesse
   */
  public async hasHeadphones(): Promise<boolean> {
    const devices = await this.getAllDevices();
    
    // Euristica: se c'è almeno un dispositivo audio input e output
    // con nome simile, probabilmente sono cuffie
    for (const input of devices.audioInputs) {
      for (const output of devices.audioOutputs) {
        if (input.label && output.label) {
          const inputLabel = input.label.toLowerCase();
          const outputLabel = output.label.toLowerCase();
          
          // Controlla se contengono parole chiave comuni per cuffie
          const headphoneKeywords = ['headphone', 'headset', 'earphone', 'airpod'];
          const hasKeyword = headphoneKeywords.some(keyword => 
            inputLabel.includes(keyword) || outputLabel.includes(keyword)
          );
          
          if (hasKeyword) return true;
          
          // Controlla se hanno nomi simili (stesso dispositivo)
          if (this.areLabelsSimilar(inputLabel, outputLabel)) {
            return true;
          }
        }
      }
    }
    
    return false;
  }
  
  private areLabelsSimilar(label1: string, label2: string): boolean {
    // Rimuovi le parti specifiche del tipo (input/output)
    const cleanLabel1 = label1.replace(/(input|output|microphone|speaker)/gi, '').trim();
    const cleanLabel2 = label2.replace(/(input|output|microphone|speaker)/gi, '').trim();

    // Se dopo la pulizia sono molto simili, probabilmente è lo stesso dispositivo
    return cleanLabel1.length > 3 && cleanLabel2.length > 3 &&
           (cleanLabel1.includes(cleanLabel2) || cleanLabel2.includes(cleanLabel1));
  }

  // ========================================
  // UTILITÀ DISPOSITIVI
  // ========================================

  /**
   * Verifica se il dispositivo supporta facingMode (per mobile)
   */
  public canUseFacingMode(): boolean {
    const supports = navigator.mediaDevices.getSupportedConstraints();
    return !!(supports['facingMode'] && this.config.isMobilePhone);
  }

  /**
   * Cambia la camera selezionata (cicla tra le disponibili)
   */
  public switchCamera(): void {
    if (this.canUseFacingMode()) {
      // Mobile: cambia orientamento
      this.selectedDevices.cameraFacing = this.selectedDevices.cameraFacing === 'user' ? 'environment' : 'user';
      console.log(`MediaDeviceService - switchCamera using facingMode '${this.selectedDevices.cameraFacing}'`);
    } else if (this.videoInputs.length > 1) {
      // Desktop: cicla tra le camere disponibili
      const currentIdx = this.videoInputs.findIndex(z => z.deviceId === this.selectedDevices.videoSourceId);
      let nextIdx = currentIdx + 1;
      if (nextIdx >= this.videoInputs.length) nextIdx = 0;

      this.selectedDevices.videoSourceId = this.videoInputs[nextIdx].deviceId;
      console.log(`MediaDeviceService - switchCamera to ${this.videoInputs[nextIdx].label} [${this.selectedDevices.videoSourceId}]`);
    }
  }

  /**
   * Torna indietro alla camera precedente (rollback di switchCamera)
   */
  public switchCameraRollback(): void {
    if (this.canUseFacingMode()) {
      // Mobile: torna all'orientamento precedente
      this.selectedDevices.cameraFacing = this.selectedDevices.cameraFacing === 'user' ? 'environment' : 'user';
      console.log(`MediaDeviceService - switchCameraRollback using facingMode '${this.selectedDevices.cameraFacing}'`);
    } else if (this.videoInputs.length > 1) {
      // Desktop: torna alla camera precedente
      const currentIdx = this.videoInputs.findIndex(z => z.deviceId === this.selectedDevices.videoSourceId);
      let prevIdx = currentIdx - 1;
      if (prevIdx < 0) prevIdx = this.videoInputs.length - 1;

      this.selectedDevices.videoSourceId = this.videoInputs[prevIdx].deviceId;
      console.log(`MediaDeviceService - switchCameraRollback to ${this.videoInputs[prevIdx].label} [${this.selectedDevices.videoSourceId}]`);
    }
  }

  // ========================================
  // GESTIONE PREFERENZE UTENTE
  // ========================================

  /**
   * Carica le preferenze utente salvate in localStorage
   */
  public loadUserPreferences(): void {
    
    const saved = localStorage.getItem(MediaDeviceService.storageKey)

    if (saved) {
      try {
        const devices = JSON.parse(saved);
        // Solo se il valore è diverso da 'default' lo consideriamo una selezione esplicita ed il devide deve essere valido

        this.userPreferredDevices = {
          audioInput: this.audioInputs.find(z => z.deviceId === devices.audioInput)?.deviceId || null, 
          audioOutput: this.audioOutputs.find(z => z.deviceId === devices.audioOutput)?.deviceId || null,
          camera: this.videoInputs.find(z => z.deviceId === devices.camera)?.deviceId || null
        };
        console.log('MediaDeviceService - loadUserPreferences - loaded:', this.userPreferredDevices);
        this.updateDeviceList();
      } catch (e) {
        console.error('MediaDeviceService - Error loading saved devices', e);
      }
    }
  }

  /**
   * Salva le preferenze utente in localStorage
   */
  private saveUserPreferences(): void {
    const toSave = {
      audioInput: this.userPreferredDevices.audioInput || DEFAULT_DEVICE,
      audioOutput: this.userPreferredDevices.audioOutput || DEFAULT_DEVICE,
      camera: this.userPreferredDevices.camera || DEFAULT_DEVICE
    };
    localStorage.setItem(MediaDeviceService.storageKey, JSON.stringify(toSave));
    console.log('MediaDeviceService - saveUserPreferences - saved:', toSave);
  }

  /**
   * Imposta le preferenze utente (chiamato da settings component)
   */
  public setPreferredDevices(devices: PreferredDevices): void {
    // Aggiorna lo stato interno delle preferenze utente
    this.userPreferredDevices = {
      audioInput: devices.audioInput,
      audioOutput: devices.audioOutput,
      camera: devices.camera
    };

    // Salva nel localStorage
    this.saveUserPreferences();

    // Aggiorna i dispositivi selezionati
    this.updateSelectedDevices();

  }

  /**
   * Ottiene le preferenze utente correnti
   */
  public getPreferredDevices(): PreferredDevices {
    return { ...this.userPreferredDevices };
  }

  /**
   * Ottiene i dispositivi selezionati con formato per constraints getUserMedia
   */
  public getSelectedDevicesForConstraints(): {
    audioInput: string | null;
    audioOutput: string | null;
    camera: string | null;
    cameraFacing: string;
  } {
    return {
      audioInput: this.selectedDevices.audioInSourceId,
      audioOutput: this.selectedDevices.audioOutSourceId,
      camera: this.selectedDevices.videoSourceId,
      cameraFacing: this.selectedDevices.cameraFacing
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.deviceChangeSubject$.complete();
  }
}